import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Icon, type IconName } from '../theme/Icon';
import s from './Button.module.css';

export type ButtonVariant = 'primary' | 'out' | 'soft' | 'danger';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Ícone do sprite antes do rótulo. */
  icon?: IconName;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: '',
  out: s.out,
  soft: s.soft,
  danger: s.dgr,
};

/**
 * Botão do design system (`.btn` e variantes).
 * Regra do CLAUDE.md: **um** `variant="primary"` por seção.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, className, children, type = 'button', ...rest },
  ref,
) {
  const classes = [s.btn, VARIANT[variant], size === 'sm' ? s.sm : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <button ref={ref} type={type} className={classes} {...rest}>
      {icon ? <Icon name={icon} size={size === 'sm' ? 'sm' : 'md'} /> : null}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Obrigatório: o botão não tem texto visível. */
  label: string;
  danger?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/** Botão só de ícone (`.ibtn`). O `label` vira `aria-label`. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, danger, className, size = 'md', type = 'button', ...rest },
  ref,
) {
  const classes = [s.ibtn, danger ? s.ibtnDanger : '', className].filter(Boolean).join(' ');
  return (
    <button ref={ref} type={type} className={classes} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size === 'md' ? 'md' : size} />
    </button>
  );
});
